import { useMemo, useState } from "react"
import { Table, Button, Form, Dropdown } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { Plus } from "lucide-react"
import toast from "react-hot-toast"

import { useDeleteClubMutation } from "../../app/clubsApiSlice"
import { useFetchClubsQuery } from "../../app/clubsApiSlice"
import Spinner from "../../components/Spinner"
import DisplayErrorMessage from "../../components/DisplayErrorMessage"
import filterBySearch from "../../utils/filterBySearch"
import filterBySortBy from "../../utils/filterBySortBy"
import formatClubName from "../../utils/formatClubName"
import TypeToConfirmButton from "../../components/buttons/TypeToConfirmButton"
import DeleteButton from "../../components/buttons/table/DeleteButton"
import ViewClubButton from "../../components/buttons/table/ViewClubButton"

const ViewClubsList = () => {
  const navigate = useNavigate()

  const { data, isSuccess, isLoading, isError, error } = useFetchClubsQuery()

  const [deleteClub, { isLoading: deleting }] = useDeleteClubMutation()

  const [sortedBy, setSortedBy] = useState("name")
  const [searchBy, setSearchBy] = useState("")
  const [reverseSort, setReverseSort] = useState(false)


  // Memoize clubsArray to make filtered data stable
  const clubsArray = useMemo(() =>
    data
      ? Object.entries(data).map(([id, data]) => ({
        name: data.clubName,
        id: id,
        email: data.secretaryEmail,
        membersCount: data.membersCount
      }))
      : [],
    [data]
  )

  // Memoize the filtered and sorted data
  const filteredAndSortedData = useMemo(() => {
    const searchData = filterBySearch(searchBy, clubsArray)
    return filterBySortBy(sortedBy, searchData, reverseSort)
  }, [searchBy, sortedBy, reverseSort, clubsArray])

  const handleCreateClub = () => navigate("/admin/clubs/create-club")

  const handleViewClub = (clubId) => navigate(`/admin/clubs/view-club/${clubId}`)

  const handleDeleteClub = async (clubId) => {
    try {
      await deleteClub(clubId).unwrap()
      toast.success("Club deleted")
    } catch (error) {
      console.log(error)
      toast.error("Error deleting club")
    }
  }

  const handleSortBy = (sortBy) => {
    if (sortedBy === sortBy) {
      setReverseSort(!reverseSort)
    } else {
      setSortedBy(sortBy)
      setReverseSort(false)
    }
  }

  if (isLoading) return <Spinner />
  if (isError) {
    console.error(error || "Error: An unknown error occurred while fetching clubs")
    return <DisplayErrorMessage>Error fetching clubs</DisplayErrorMessage>
  }
  if (!isSuccess || !data) return null

  // Create rows for the table
  let tableContent

  if (filteredAndSortedData.length < 1) {
    tableContent = (
      <tr>
        <td colSpan={6} className="text-center">
          No clubs found
        </td>
      </tr>
    )
  } else {
    tableContent = filteredAndSortedData.map((club) => {
      return (
        <tr key={club.id}>
          <td>{formatClubName(club.name)}</td>
          <td>{club.id}</td>
          <td style={{ fontSize: "14px" }}>{club.membersCount}</td>
          <td>{club.email}</td>
          <td>
            <div className="d-flex gap-2 p-0 m-0 justify-content-center">
              <ViewClubButton action={() => handleViewClub(club.id)} />
              <TypeToConfirmButton
                action={() => handleDeleteClub(club.id)}
                isLoading={deleting}
                title="Delete club?"
                message="This club will be permanently deleted from the database and cannot be recovered."
                buttonText="Delete"
                confirmMessage="Please type club number to confirm."
                confirmString={club.id}
              >
                <DeleteButton />
              </TypeToConfirmButton>
            </div>
          </td>
        </tr>
      )
    })
  }

  return (
    <>
      <h3 className="mb-4 text-start w-100 fw-bold">Clubs List</h3>
      {/* BUTTONS */}
      <div className="d-flex flex-wrap gap-3 w-100 mb-2">

        {/* Search bar */}
        <Form style={{ width: "200px" }}>
          <Form.Control
            className="border-0 shadow-sm fw-semibold rounded-3"
            type="text"
            placeholder="Search"
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
          />
        </Form>

        {/* Sort by dropdown */}
        <Dropdown>
          <Dropdown.Toggle className="bg-white border-0 rounded-3 shadow-sm text-muted fw-semibold" id="dropdown-basic">
            Sort by
          </Dropdown.Toggle>
          <Dropdown.Menu className="border-0 shadow">
            <Dropdown.Item as="button" onClick={() => handleSortBy("name")}>Name</Dropdown.Item>
            <Dropdown.Item as="button" onClick={() => handleSortBy("num")}>Club Num</Dropdown.Item>
            <Dropdown.Item as="button" onClick={() => handleSortBy("email")}>Email</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        {/* Create club button */}
        <Button className="d-flex align-items-center rounded-3 gap-2 border-0" variant="dark" onClick={handleCreateClub}>
          <Plus size={20} color="white" strokeWidth={2} className="align-self-center" />
          Create Club
        </Button>
      </div>

      <div className="overflow-auto w-100 rounded-4 py-4 px-4 shadow-sm bg-white">
        <Table hover responsive className="mb-0 mt-1" style={{ minWidth: "640px" }}>
          <thead>
            <tr>
              <th className="col-2">Club Name</th>
              <th className="col-2">Club Num</th>
              <th className="col-2">Members</th>
              <th className="col-2">Secretary</th>
              <th className="col-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableContent}
          </tbody>
        </Table>
      </div>
    </>
  )
}

export default ViewClubsList